/* ------------------------------------------------------------------
 * Reading progress bar
 * Tracks scroll position relative to the article content and updates
 * the width of #article-progress-bar > .article-new__progress-bar-fill.
 * ------------------------------------------------------------------ */
(function () {
  const MIN_VISIBLE_PROGRESS = 10;
  let initialized = false;
  let ticking = false;
  let bar = null;
  let fill = null;

  const update = () => {
    if (!bar || !fill) {
      ticking = false;
      return;
    }

    const main = document.getElementById('MainContent');
    if (!main) { ticking = false; return; }
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const winHeight = window.innerHeight || document.documentElement.clientHeight;
    const mainTop = main.getBoundingClientRect().top + scrollTop;
    const mainBottom = mainTop + main.offsetHeight;
    const scrollable = Math.max(mainBottom - winHeight, 1);
    const scrolled = scrollTop - mainTop;
    const ratio = Math.min(Math.max(scrolled / scrollable, 0), 1);
    const percent = MIN_VISIBLE_PROGRESS + ratio * (100 - MIN_VISIBLE_PROGRESS);
    fill.style.width = percent + '%';
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    window.requestAnimationFrame(update);
    ticking = true;
  };

  const init = () => {
    bar = document.getElementById('article-progress-bar');
    if (!bar) return false;

    fill = bar.querySelector('.article-new__progress-bar-fill');
    if (!fill) return false;

    bar.hidden = false;
    fill.hidden = false;
    bar.removeAttribute('hidden');
    fill.removeAttribute('hidden');
    bar.style.display = 'block';
    bar.style.visibility = 'visible';
    bar.style.opacity = '1';
    fill.style.display = 'block';
    fill.style.visibility = 'visible';
    fill.style.opacity = '1';

    if (!initialized) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      initialized = true;
    }

    onScroll();
    return true;
  };

  const initWithRetry = () => {
    if (init()) return;

    let attempts = 0;
    const maxAttempts = 180;
    const retry = () => {
      attempts += 1;
      if (init() || attempts >= maxAttempts) return;
      window.requestAnimationFrame(retry);
    };

    window.requestAnimationFrame(retry);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithRetry, { once: true });
  } else {
    initWithRetry();
  }

  document.addEventListener('shopify:section:load', initWithRetry);
  document.addEventListener('shopify:section:reorder', initWithRetry);
})();

/* ------------------------------------------------------------------
 * Article audio player custom element
 * Wires the play/pause button, progress bar, and time labels to the
 * underlying <audio> element rendered inside the component.
 * ------------------------------------------------------------------ */
class ArticleAudioPlayer extends HTMLElement {
  constructor() {
    super();
    this._onPlay = this._onPlay.bind(this);
    this._onPause = this._onPause.bind(this);
    this._onTimeUpdate = this._onTimeUpdate.bind(this);
    this._onLoaded = this._onLoaded.bind(this);
    this._onTrackClick = this._onTrackClick.bind(this);
    this._onPlayClick = this._onPlayClick.bind(this);
  }

  connectedCallback() {
    this.audio = this.querySelector('.article-new__audio-element');
    this.playButton = this.querySelector('.article-new__audio-play');
    this.iconPlay = this.querySelector('.article-new__audio-icon--play');
    this.iconPause = this.querySelector('.article-new__audio-icon--pause');
    this.progress = this.querySelector('.article-new__audio-progress');
    this.track = this.querySelector('.article-new__audio-track');
    this.currentEl = this.querySelector('.article-new__audio-current');
    this.durationEl = this.querySelector('.article-new__audio-duration');

    if (!this.audio || !this.playButton) return;

    this.playButton.addEventListener('click', this._onPlayClick);
    this.audio.addEventListener('play', this._onPlay);
    this.audio.addEventListener('pause', this._onPause);
    this.audio.addEventListener('ended', this._onPause);
    this.audio.addEventListener('timeupdate', this._onTimeUpdate);
    this.audio.addEventListener('loadedmetadata', this._onLoaded);
    if (this.track) this.track.addEventListener('click', this._onTrackClick);
  }

  disconnectedCallback() {
    if (!this.audio) return;
    this.playButton.removeEventListener('click', this._onPlayClick);
    this.audio.removeEventListener('play', this._onPlay);
    this.audio.removeEventListener('pause', this._onPause);
    this.audio.removeEventListener('ended', this._onPause);
    this.audio.removeEventListener('timeupdate', this._onTimeUpdate);
    this.audio.removeEventListener('loadedmetadata', this._onLoaded);
    if (this.track) this.track.removeEventListener('click', this._onTrackClick);
  }

  _onPlayClick() {
    if (this.audio.paused) {
      this.audio.play();
    } else {
      this.audio.pause();
    }
  }

  _onPlay() {
    if (this.iconPlay) this.iconPlay.hidden = true;
    if (this.iconPause) this.iconPause.hidden = false;
    this.playButton.setAttribute('aria-label', 'Pause audio');
  }

  _onPause() {
    if (this.iconPlay) this.iconPlay.hidden = false;
    if (this.iconPause) this.iconPause.hidden = true;
    this.playButton.setAttribute('aria-label', 'Play audio');
  }

  _onLoaded() {
    if (this.durationEl) {
      this.durationEl.textContent = this._format(this.audio.duration);
    }
  }

  _onTimeUpdate() {
    const duration = this.audio.duration || 0;
    const current = this.audio.currentTime || 0;
    if (this.currentEl) this.currentEl.textContent = this._format(current);
    if (this.progress && duration > 0) {
      this.progress.style.width = (current / duration) * 100 + '%';
    }
  }

  _onTrackClick(event) {
    const duration = this.audio.duration;
    if (!duration || !isFinite(duration)) return;
    const rect = this.track.getBoundingClientRect();
    const position = (event.clientX - rect.left) / rect.width;
    this.audio.currentTime = Math.min(Math.max(position, 0), 1) * duration;
  }

  _format(seconds) {
    if (!seconds || !isFinite(seconds)) return '00:00';
    const total = Math.floor(seconds);
    const mins = Math.floor(total / 60);
    const secs = total % 60;
    return String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');
  }
}

if (!customElements.get('article-audio-player')) {
  customElements.define('article-audio-player', ArticleAudioPlayer);
}
